import { clamp, mapRange } from 'gsap/all'
import { module } from 'modujs'
import HorizontalView from '../classes/HorizontalView'

const MQ = 1200

export default class extends module {
    constructor(m) {
        super(m)

        // Binding
        this.onResizeBind = this.onResize.bind(this)

        // UI
        this.$el = this.el
        this.$slider = this.$('slider')[0]
        this.$root = this.$('root')[0]
        this.$slides = this.$('slide')

        // Data
        this.sidebarWidth = 0
        this.pixelsScrolled = 0
        this.views = []
        this.slideMetrics = []

        this.hasInit = false
    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        this.bindEvents()

        if (window.innerWidth >= MQ && !this.hasInit) {
            this.hasInit = true
            this._init()
        }
    }

    destroy() {
        super.destroy()
        this.unbindEvents()

        if (this.hasInit) {
            this._destroy()
        }
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener('resize', this.onResizeBind)
    }

    unbindEvents() {
        window.removeEventListener('resize', this.onResizeBind)
    }

    ///////////////
    // Callbacks
    ///////////////
    onResize() {
        if (window.innerWidth >= MQ && !this.hasInit) {
            const styledElements = this.$el.querySelectorAll('[style]')
            let styledElementsIndex = 0
            while (styledElementsIndex < styledElements.length) {
                styledElements[styledElementsIndex].style = ''
                styledElementsIndex++
            }

            requestAnimationFrame(() => {
                this.hasInit = true
                this._init()
            })
        }

        if (window.innerWidth < MQ && this.hasInit) {
            const styledElements = this.$el.querySelectorAll('[style]')
            let styledElementsIndex = 0
            while (styledElementsIndex < styledElements.length) {
                styledElements[styledElementsIndex].style = ''
                styledElementsIndex++
            }

            requestAnimationFrame(() => {
                this.hasInit = false
                this._destroy()
            })
        }

        if (this.hasInit) {
            requestAnimationFrame(() => {
                this.computeScrollHeight()
                this.computeSlideMetrics()
                this.resizeViews()
            })
        }
    }

    onProgress(progress) {
        if (!this.scrollHeight) return
        this.pixelsScrolled = clamp(0, this.scrollHeight, mapRange(0, 1, 0, this.scrollHeight, progress))
        let index = 0

        while (index < this.$slides.length) {
            const metrics = this.slideMetrics[index]

            if (this.pixelsScrolled >= metrics.start && this.pixelsScrolled <= metrics.end) {
                const localPixelsScrolled = mapRange(metrics.start, metrics.end, 0, metrics.width, this.pixelsScrolled)
                const computedPixelsScrolled = localPixelsScrolled - metrics.originX
                this.$slides[index].style.transform = `translate3d(${computedPixelsScrolled * -1}px, 0, 0)`


                if (!metrics.isVisible) {
                    this.$slides[index].style.visibility = ''

                    metrics.isVisible = true                    

                    // Enable interactive view
                    let viewIndex = 0
                    while (viewIndex < this.views.length) {
                        const view = this.views[viewIndex]

                        if (view.obj.parentIndex == index) {
                            view.obj.isInteractive = true
                        }
                        viewIndex++
                    }
                }


            } else if (this.pixelsScrolled < metrics.start) {
                this.$slides[index].style.transform = `translate3d(${metrics.originX}px, 0, 0)`

                if (metrics.isVisible) {
                    this.$slides[index].style.visibility = `hidden`
                    metrics.isVisible = false

                    // Disable interactive view
                    let viewIndex = 0
                    while (viewIndex < this.views.length) {
                        const view = this.views[viewIndex]

                        if (view.obj.parentIndex == index) {
                            view.obj.isInteractive = false
                        }
                        viewIndex++
                    }
                }

            } else if (this.pixelsScrolled > metrics.end) {

                if (metrics.isVisible) {
                    this.$slides[index].style.visibility = `hidden`
                    metrics.isVisible = false

                    // Disable interactive view
                    let viewIndex = 0
                    while (viewIndex < this.views.length) {
                        const view = this.views[viewIndex]

                        if (view.obj.parentIndex == index) {
                            view.obj.isInteractive = false
                        }
                        viewIndex++
                    }
                }
            }

            index++
        }

        this.scrollViews()
    }

    ///////////////
    // Methods
    ///////////////
    _init() {
        // Retrieve views
        const $views = this.$el.querySelectorAll("[data-hscroll]")

        // For each view create a View instance
        let index = 0
        while (index < $views.length) {
            const $view = $views[index]
            let $viewParent, viewParentIndex

            let slideIndex = 0
            while (slideIndex < this.$slides.length) {
                const $slide = this.$slides[slideIndex]
                if ($slide.contains($view)) {
                    $viewParent = $slide
                    viewParentIndex = slideIndex
                    break;
                }
                slideIndex++;
            }

            const viewInstance = new HorizontalView($view, $viewParent, viewParentIndex)
            this.views.push({ index, obj: viewInstance })
            index++
        }

        requestAnimationFrame(() => {
            this.computeScrollHeight()
            this.computeSlideMetrics()
            this.onProgress(0)
            this.resizeViews()
        })
    }

    _destroy() {
        this.unsubscribeAllViews()
    }

    computeScrollHeight() {
        this.viewWidth = this.$el.offsetWidth

        let index = 0
        let sliderWidth = 0
        while (index < this.$slides.length) {
            sliderWidth += this.$slides[index].offsetWidth
            index++
        }

        this.scrollHeight = sliderWidth - this.viewWidth
        this.$el.style.setProperty("--scroll-height", `${this.scrollHeight}px`)

        requestAnimationFrame(() => {
            this.call('update', null, 'Scroll')
        })
    }

     computeSlideMetrics() {
        this.slideMetrics = []

        let offset = 0
        let index = 0

        while (index < this.$slides.length) {
            this.slideMetrics.push({
                index: index,
                rootWidth: this.$root.offsetWidth,
                start: offset,
                end: index > 0 && index < this.$slides.length - 1 ? offset + this.$slides[index].offsetWidth + this.$root.offsetWidth : offset + this.$slides[index].offsetWidth,
                originX: index < 1 ? 0 : this.$root.offsetWidth,
                width: index > 0 && index < this.$slides.length - 1 ? this.$slides[index].offsetWidth + this.$root.offsetWidth : this.$slides[index].offsetWidth,
                isVisible: index < 1 ? false : true
            })

            offset += index < 1 ? this.$slides[index].offsetWidth - this.$root.offsetWidth : this.$slides[index].offsetWidth
            index++
        }
    }

    scrollViews() {
        let index = 0
        while (index < this.views.length) {
            const $view = this.views[index]
            $view.obj.onScroll(this.pixelsScrolled)
            index++
        }
    }

    resizeViews() {
        let index = 0
        while (index < this.views.length) {
            const $view = this.views[index]
            $view.obj.onResize(this.pixelsScrolled, this.slideMetrics)
            index++
        }
    }

    unsubscribeAllViews() {
        let index = 0
        while (index < this.views.length) {
            const view = this.views[index]
            view.obj.destroy()
            index++
        }

        // clear array
        this.views = []
    }
}
